/**
 * Phase 11 Plan 05 — D-16 `billing_status` badge.
 *
 * Pure derivation from props (no data fetching). The consumer is responsible
 * for computing `has_unresolved_dispute` (see ClientBillingDetailPanel and
 * CommandCenter queries for the rule: an unresolved `dispute.created` row in
 * `fanbasis_events` for this client without a later `dispute.updated` row
 * carrying `status='resolved'`).
 *
 * State precedence (first match wins):
 *   1. has_unresolved_dispute → "Disputed" (error)
 *   2. !has_payment_method    → "No payment method" (error)
 *   3. billing_mode=legacy_cron    → "Legacy cron" (default/muted)
 *   4. billing_mode=direct_charge  → "Active (direct_charge)" (success)
 *   5. billing_mode=subscription:
 *        status=past_due              → "Past Due" (warning)
 *        status=canceled | completed  → "Canceled" (default/muted)
 *        otherwise (incl. 'active')   → "Active (subscription)" (success)
 *
 * BadgeVenture variant mapping (the primitive has no literal `danger`/`muted`
 * variants; we use `error` for danger and `default` for muted — see
 * src/components/ui/badge-venture.tsx).
 */
import { BadgeVenture } from '@/components/ui/badge-venture';

export interface BillingStatusBadgeProps {
  billing_mode: 'legacy_cron' | 'subscription' | 'direct_charge';
  subscription_status?: 'active' | 'past_due' | 'canceled' | 'completed' | null;
  has_payment_method: boolean;
  has_unresolved_dispute?: boolean;
}

export function BillingStatusBadge({
  billing_mode,
  subscription_status,
  has_payment_method,
  has_unresolved_dispute,
}: BillingStatusBadgeProps) {
  if (has_unresolved_dispute) {
    return <BadgeVenture variant="error">Disputed</BadgeVenture>;
  }
  if (!has_payment_method) {
    return <BadgeVenture variant="error">No payment method</BadgeVenture>;
  }
  if (billing_mode === 'legacy_cron') {
    return <BadgeVenture variant="default">Legacy cron</BadgeVenture>;
  }
  if (billing_mode === 'direct_charge') {
    return <BadgeVenture variant="success">Active (direct_charge)</BadgeVenture>;
  }
  // billing_mode === 'subscription'
  if (subscription_status === 'past_due') {
    return <BadgeVenture variant="warning">Past Due</BadgeVenture>;
  }
  if (subscription_status === 'canceled' || subscription_status === 'completed') {
    return <BadgeVenture variant="default">Canceled</BadgeVenture>;
  }
  return <BadgeVenture variant="success">Active (subscription)</BadgeVenture>;
}
